import type { InteractionResolver } from "@repo/app-types/content/structured/parser";
import {
  playbookIdSchema,
  type PlaybookId,
} from "@repo/app-types/entities/playbook";
import {
  teammateIdSchema,
  type TeammateId,
} from "@repo/app-types/entities/teammate";
import {
  gradingIdSchema,
  type GradingId,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import type { UIMessage } from "ai";
import type { z } from "zod";

export interface ToolResultEntityResolvers {
  /** Resolves `<quote>`/`<interaction>` interaction ids to their grading. */
  resolveInteractionId: InteractionResolver;
  /** Resolves a `<teammate>` id to the playbook its page is scoped to. */
  resolveTeammatePlaybookId: (teammateId: TeammateId) => PlaybookId | undefined;
}

/**
 * The one output that breaks the naming convention below: get-customer-
 * interaction (under its Ask Alpharun and automation toolset keys, see
 * `toolRegistry.ts`) returns the interaction itself, so its interaction id is
 * the top-level `id`. Stored threads keep the old shape, so it is aliased
 * here rather than changed at the tool.
 */
const CUSTOMER_INTERACTION_TOOL_NAMES: ReadonlySet<string> = new Set([
  "getCustomerInteractionTool",
  "getCustomerInteraction",
]);

interface ToolResult {
  toolName: string;
  input: unknown;
  output: unknown;
}

/**
 * Reads a finished tool part's name, input and output. The AI SDK encodes a
 * static tool part as `type: "tool-<name>"` and a dynamic one as
 * `type: "dynamic-tool"` with a `toolName` field; Mastra sends both.
 */
function getToolResult(
  part: UIMessage["parts"][number],
): ToolResult | undefined {
  const toolName =
    part.type === "dynamic-tool"
      ? part.toolName
      : part.type.startsWith("tool-")
        ? part.type.slice("tool-".length)
        : undefined;

  if (toolName === undefined) {
    return undefined;
  }

  const toolPart = part as {
    state?: string;
    input?: unknown;
    output?: unknown;
  };
  if (toolPart.state !== "output-available") {
    return undefined;
  }

  return { toolName, input: toolPart.input, output: toolPart.output };
}

interface EntitySink {
  interactionsByGradingId: Map<
    GradingId,
    { gradingId: GradingId; playbookId: PlaybookId }
  >;
  playbookByTeammate: Map<TeammateId, PlaybookId>;
}

/**
 * Builds the resolvers for the entity tags in an agent's messages from the
 * tool results in the same thread. Tool outputs share one naming convention:
 * an interaction is any `interaction_id`, a teammate is any `teammate_id` or
 * `teammate: { id }`, and each belongs to the nearest enclosing `playbook_id`
 * (or `playbook: { id }`), falling back to the tool input's `playbook_id`.
 * Entities are collected from anywhere in an output, so a new tool needs no
 * registration here as long as it follows the convention. Entities with no
 * playbook in scope are skipped, since every link target is playbook-scoped.
 */
export function buildEntityResolversFromToolResults(
  messages: readonly UIMessage[],
): ToolResultEntityResolvers {
  const sink: EntitySink = {
    interactionsByGradingId: new Map(),
    playbookByTeammate: new Map(),
  };

  for (const message of messages) {
    for (const part of message.parts) {
      const tool = getToolResult(part);
      if (!tool) {
        continue;
      }
      const output =
        CUSTOMER_INTERACTION_TOOL_NAMES.has(tool.toolName) &&
        isRecord(tool.output)
          ? { ...tool.output, interaction_id: tool.output.id }
          : tool.output;
      collectEntities(output, readPlaybookId(tool.input), sink);
    }
  }

  return {
    resolveInteractionId: (interactionId) =>
      sink.interactionsByGradingId.get(interactionId as GradingId),
    resolveTeammatePlaybookId: (teammateId) =>
      sink.playbookByTeammate.get(teammateId),
  };
}

function collectEntities(
  value: unknown,
  playbookIdInScope: PlaybookId | undefined,
  sink: EntitySink,
): void {
  if (Array.isArray(value)) {
    for (const item of value) {
      collectEntities(item, playbookIdInScope, sink);
    }
    return;
  }
  if (!isRecord(value)) {
    return;
  }

  const playbookId = readPlaybookId(value) ?? playbookIdInScope;

  const interactionId = parseId(gradingIdSchema, value.interaction_id);
  if (interactionId && playbookId) {
    sink.interactionsByGradingId.set(interactionId, {
      gradingId: interactionId,
      playbookId,
    });
  }

  const teammateId =
    parseId(teammateIdSchema, value.teammate_id) ??
    (isRecord(value.teammate)
      ? parseId(teammateIdSchema, value.teammate.id)
      : undefined);
  if (teammateId && playbookId) {
    sink.playbookByTeammate.set(teammateId, playbookId);
  }

  for (const child of Object.values(value)) {
    collectEntities(child, playbookId, sink);
  }
}

function readPlaybookId(value: unknown): PlaybookId | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  return (
    parseId(playbookIdSchema, value.playbook_id) ??
    (isRecord(value.playbook)
      ? parseId(playbookIdSchema, value.playbook.id)
      : undefined)
  );
}

function parseId<S extends z.ZodType>(
  schema: S,
  value: unknown,
): z.output<S> | undefined {
  return typeof value === "string" ? schema.safeParse(value).data : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
